import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator, Platform, ScrollView, StyleSheet, TouchableOpacity,
  useWindowDimensions, View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/types';
import { useTheme } from '../context/ThemeContext';
import { Typography } from '../components/ui';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { api } from '../services/api';
import { fetchUnreadNotificationsCount, notifyUnreadNotificationsChanged } from '../hooks/useUnreadNotifications';
import {
  formatNotificationAge, groupNotifications, isActionable, notificationIcon,
  notificationTone, resolveNotificationRoute, type NotificationTone,
} from '../components/notifications/notificationRouting';
import { appendFeedPage, fetchFeedPageAfter, mergeFeedHead } from '../components/notifications/notificationFeed';
import type { AppNotification } from '../types/notifications';
import { openUrlInApp } from '../utils/sameOriginUrl';
import { parseLiveChatLink } from '../utils/liveChatLink';
import { trackLiveChatNotificationOpened } from '../services/liveChatNotificationTelemetry';
import { MONEY_SURFACE_ENABLED, PROGRAMS_SURFACE_ENABLED } from '../config/flags';

import { ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
const ROUTE_CTX = {
  isWeb: Platform.OS === 'web',
  programsEnabled: PROGRAMS_SURFACE_ENABLED,
  moneyEnabled: MONEY_SURFACE_ENABLED,
};

const PAGE_SIZE = 30;
// CORE-883 (QA 23.10) — как часто открытая лента сверяет свой верх с сервером.
// Тот же шаг, что у колокольчика в шапке: число в нём и строки на экране не
// должны расходиться дольше минуты.
const HEAD_REFRESH_MS = 60_000;
const WEB = Platform.OS === 'web';

type Nav = NativeStackNavigationProp<RootStackParamList>;
type Filter = 'all' | 'unread';

function dayStamp(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function notificationTime(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const dayDiff = Math.round((dayStamp(now) - dayStamp(date)) / 86_400_000);
  if (dayDiff === 1) {
    return `Вчера, ${new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date)}`;
  }
  return formatNotificationAge(iso, now);
}

export default function NotificationsScreen() {
  const { colors: C, isDark } = useTheme();
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const compact = useWindowDimensions().width < 640;

  // CORE-883 (QA 23.10) — список и «есть ли дальше» меняются одним шагом:
  // сверка верха и дозагрузка низа решают оба сразу. «Дальше» — ответ
  // сервера, а не «строк меньше, чем totalCount»: отозванная строка на экране
  // сделала бы это сравнение ложным и спрятала бы кнопку.
  const [list, setList] = useState<{ items: AppNotification[]; hasMore: boolean }>({ items: [], hasMore: false });
  const items = list.items;
  const setItems = useCallback((next: AppNotification[] | ((prev: AppNotification[]) => AppNotification[])) => {
    setList((cur) => ({ ...cur, items: typeof next === 'function' ? next(cur.items) : next }));
  }, []);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [menuId, setMenuId] = useState<number | null>(null);
  const requestId = useRef(0);
  // «Прочитать все» меняет список целиком (в «Непрочитанных» — очищает).
  // Ответ дозагрузки или сверки, ушедший раньше, вернул бы прочитанное
  // обратно — его выбрасываем.
  const readAllEpoch = useRef(0);
  const refreshUnreadOnFocus = useRef(false);

  const load = useCallback(async (nextFilter: Filter) => {
    const id = ++requestId.current;
    setLoading(true);
    setFailed(false);
    setMenuId(null);
    try {
      const [feed, unreadResult] = await Promise.all([
        api.getNotifications({ limit: PAGE_SIZE, offset: 0, unreadOnly: nextFilter === 'unread' }),
        fetchUnreadNotificationsCount().catch(() => null),
      ]);
      if (id !== requestId.current) return;
      const firstPage = feed.items ?? [];
      setList({ items: firstPage, hasMore: firstPage.length < (feed.totalCount ?? 0) });
      setUnreadCount(unreadResult?.count ?? (nextFilter === 'unread'
        ? feed.totalCount ?? 0
        : (feed.items ?? []).filter((n) => !n.isRead).length));
    } catch {
      if (id === requestId.current) setFailed(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(filter); }, [filter, load]);

  // Keep the exact focus reset used by the lazy-route feedback contract.
  useFocusEffect(useCallback(() => { setPendingId(null); }, []));
  useFocusEffect(useCallback(() => {
    if (!refreshUnreadOnFocus.current) return;
    refreshUnreadOnFocus.current = false;
    void load(filter);
  }, [filter, load]));

  // CORE-883 (QA 23.10) — дописываем от последней показанной строки, а не от
  // их числа, и тем же нажатием сверяем верх. Иначе новое уведомление между
  // страницами сдвигало смещение: последняя строка приходила второй раз, а
  // само новое не появлялось до перезагрузки (см. notificationFeed).
  const loadMore = useCallback(async () => {
    if (loadingMore || !list.hasMore) return;
    const id = requestId.current;
    const epoch = readAllEpoch.current;
    const unreadOnly = filter === 'unread';
    setLoadingMore(true);
    try {
      const [page, head, unreadResult] = await Promise.all([
        fetchFeedPageAfter(
          list.items,
          (offset, limit) => api.getNotifications({ limit, offset, unreadOnly }),
          PAGE_SIZE,
        ),
        // Верх — попутно: без него страница всё равно дописывается верно.
        api.getNotifications({ limit: PAGE_SIZE, offset: 0, unreadOnly }).catch(() => null),
        fetchUnreadNotificationsCount().catch(() => null),
      ]);
      if (id !== requestId.current || epoch !== readAllEpoch.current) return;
      setList((cur) => {
        const appended = appendFeedPage(cur.items, page.items);
        return head ? mergeFeedHead(appended, head, page.hasMore) : { items: appended, hasMore: page.hasMore };
      });
      if (unreadResult) setUnreadCount(unreadResult.count);
    } catch {
      // Existing rows remain usable; the same button retries the request.
    } finally {
      setLoadingMore(false);
    }
  }, [filter, list, loadingMore]);

  // CORE-883 (QA 23.10) — новое уведомление появляется само, пока лента
  // открыта, а не после перезагрузки.
  const refreshHead = useCallback(async () => {
    const id = requestId.current;
    const epoch = readAllEpoch.current;
    try {
      const [head, unreadResult] = await Promise.all([
        api.getNotifications({ limit: PAGE_SIZE, offset: 0, unreadOnly: filter === 'unread' }),
        fetchUnreadNotificationsCount().catch(() => null),
      ]);
      if (id !== requestId.current || epoch !== readAllEpoch.current) return;
      setList((cur) => mergeFeedHead(cur.items, head, cur.hasMore));
      if (unreadResult) setUnreadCount(unreadResult.count);
    } catch {
      // Молча: строки на экране остаются, следующая сверка повторит запрос.
    }
  }, [filter]);

  useFocusEffect(useCallback(() => {
    const timer = setInterval(() => { void refreshHead(); }, HEAD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refreshHead]));

  const markAllRead = useCallback(async () => {
    const previousList = list;
    const previousUnreadCount = unreadCount;
    readAllEpoch.current += 1;
    setUnreadCount(0);
    setMenuId(null);
    if (filter === 'unread') {
      setList({ items: [], hasMore: false });
    } else {
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    }
    try {
      await api.markAllNotificationsRead();
      notifyUnreadNotificationsChanged();
    } catch {
      setList(previousList);
      setUnreadCount(previousUnreadCount);
      await load(filter);
    }
  }, [filter, list, load, setItems, unreadCount]);

  const markOneRead = useCallback(async (n: AppNotification) => {
    if (n.isRead) return;
    setMenuId(null);
    setUnreadCount((count) => Math.max(0, count - 1));
    if (filter === 'unread') {
      setItems((prev) => prev.filter((item) => item.id !== n.id));
    } else {
      setItems((prev) => prev.map((item) => (item.id === n.id ? { ...item, isRead: true } : item)));
    }
    try {
      await api.markNotificationRead(n.id);
      notifyUnreadNotificationsChanged();
    } catch {
      await load(filter);
    }
  }, [filter, load, setItems]);

  const open = useCallback(async (n: AppNotification) => {
    setMenuId(null);
    if (!n.isRead) {
      if (filter === 'unread') refreshUnreadOnFocus.current = true;
      setUnreadCount((count) => Math.max(0, count - 1));
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      void api.markNotificationRead(n.id).then(() => notifyUnreadNotificationsChanged()).catch(() => undefined);
    }

    const route = resolveNotificationRoute(n, ROUTE_CTX);
    if (route.kind !== 'unavailable') setPendingId(n.id);
    const chatLink = parseLiveChatLink(n.deeplink);
    if (chatLink && route.kind === 'screen') trackLiveChatNotificationOpened('inbox', chatLink, n.type);
    if (route.kind === 'screen') {
      (navigation.navigate as any)(route.name, route.params);
    } else if (route.kind === 'link') {
      await openUrlInApp(route.url);
    }
  }, [filter, navigation, setItems]);

  const toneColor = useCallback((tone: NotificationTone): string => {
    switch (tone) {
      case 'alert': return C.error;
      case 'positive': return C.success;
      default: return C.textPrimary;
    }
  }, [C.error, C.success, C.textPrimary]);

  const now = useMemo(() => new Date(), []);
  const groups = useMemo(() => groupNotifications(items, now), [items, now]);
  const unreadLabel = unreadCount > 99
    ? '99+ новых'
    : `${unreadCount} ${unreadCount % 10 === 1 && unreadCount % 100 !== 11 ? 'новое' : 'новых'}`;
  const cardSurface = isDark ? 'rgba(59, 47, 39, 0.46)' : 'rgba(255, 251, 247, 0.70)';
  const readSurface = isDark ? 'rgba(38, 37, 36, 0.48)' : 'rgba(255, 255, 255, 0.52)';
  const cardBorder = isDark ? 'rgba(241, 181, 142, 0.25)' : 'rgba(214, 153, 113, 0.25)';
  const cardShadow = isDark
    ? 'inset 0 1px 0 rgba(255,255,255,0.035), 0 16px 42px rgba(0,0,0,0.18)'
    : 'inset 0 1px 0 rgba(255,255,255,0.86), 0 16px 38px rgba(91,62,43,0.075)';
  const stopEvent = (event: any) => event?.stopPropagation?.();

  return (
    <View style={[s.root, { backgroundColor: C.pageBg }]} testID="notifications-screen">
      <Header />
      {Platform.OS !== 'web' && (
        <View style={[s.nativeBar, { paddingTop: insets.top + 4, backgroundColor: C.canvas }]}>
          <TouchableOpacity
            testID="notifications-back"
            accessibilityRole="button" accessibilityLabel="Назад" hitSlop={8}
            onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Home'))}
            style={s.nativeBack}
          >
            <Ionicons name="chevron-back" size={24} color={C.textPrimary} />
          </TouchableOpacity>
          <Typography style={{ color: C.textPrimary }}>Уведомления</Typography>
        </View>
      )}

      <ScrollView
        style={s.scroll}
        contentContainerStyle={s.pageFlow}
        keyboardShouldPersistTaps="handled"
        onScrollBeginDrag={() => setMenuId(null)}
      >
        <View style={[
          s.content, compact && s.contentCompact,
          Platform.OS !== 'web' && { paddingTop: 20, paddingHorizontal: 16 },
        ]}>
          <View style={[s.head, compact && s.headCompact]}>
            <View style={s.titleLine}>
              <Typography variant={compact ? 'h3' : 'h2'} style={{ color: C.textPrimary }}>Уведомления</Typography>
              <View
                testID="notificationsUnreadCount"
                style={[
                  s.unreadBadge,
                  {
                    backgroundColor: isDark ? 'rgba(226, 106, 57, 0.24)' : 'rgba(244, 174, 125, 0.25)',
                    borderColor: isDark ? 'rgba(245, 139, 91, 0.30)' : 'rgba(233, 130, 69, 0.12)',
                  },
                ]}
              >
                <Typography variant="overline" style={{ color: isDark ? '#F7DED0' : C.textPrimary }}>{unreadLabel}</Typography>
              </View>
            </View>

            <TouchableOpacity
              onPress={() => void markAllRead()} disabled={unreadCount === 0}
              accessibilityRole="button" accessibilityLabel="Прочитать все уведомления"
              testID="notificationsMarkAllRead" activeOpacity={0.65}
              style={[s.readAll, unreadCount === 0 && s.readAllDisabled]}
            >
              <Ionicons name="checkmark-circle-outline" size={22} color={unreadCount > 0 ? ACCENT_TEXT : C.textMuted} />
              <Typography variant="body" style={{ color: unreadCount > 0 ? C.textPrimary : C.textMuted }}>Прочитать все</Typography>
            </TouchableOpacity>
          </View>

          <View
            style={[
              s.filters, compact && s.filtersCompact,
              {
                backgroundColor: isDark ? 'rgba(31, 29, 28, 0.44)' : 'rgba(255,255,255,0.36)',
                borderColor: C.borderSubtle,
              },
              WEB && ({ backdropFilter: 'blur(16px) saturate(135%)' } as any),
            ]}
            accessibilityRole="tablist"
          >
            {([['all', 'Все'], ['unread', 'Непрочитанные']] as const).map(([value, label]) => {
              const selected = filter === value;
              return (
                <TouchableOpacity
                  key={value} testID={`notificationsFilter-${value}`}
                  accessibilityRole="tab" accessibilityLabel={label} accessibilityState={{ selected }}
                  onPress={() => setFilter(value)} activeOpacity={0.8}
                  style={[
                    s.filter,
                    selected && {
                      backgroundColor: isDark ? 'rgba(90, 67, 50, 0.46)' : 'rgba(255,255,255,0.76)',
                      borderColor: isDark ? 'rgba(241, 181, 142, 0.24)' : 'rgba(225, 180, 146, 0.22)',
                    },
                    selected && WEB && ({
                      boxShadow: isDark
                        ? 'inset 0 1px 0 rgba(255,255,255,.05), 0 4px 14px rgba(0,0,0,.18)'
                        : 'inset 0 1px 0 rgba(255,255,255,.9), 0 4px 14px rgba(80,55,36,.08)',
                    } as any),
                  ]}
                >
                  <Typography variant="body" style={{ color: selected ? ACCENT_TEXT : C.textMuted }}>{label}</Typography>
                </TouchableOpacity>
              );
            })}
          </View>

          {loading ? (
            <View style={s.stateBox}><ActivityIndicator color={ACCENT_TEXT} /></View>
          ) : failed ? (
            <View style={s.stateBox}>
              <Ionicons name="cloud-offline-outline" size={30} color={C.textMuted} />
              <Typography variant="body" align="center" style={{ color: C.textSecondary }}>Не получилось загрузить уведомления.</Typography>
              <TouchableOpacity onPress={() => void load(filter)} accessibilityRole="button" style={[s.retry, { borderColor: C.border }]}>
                <Typography variant="body" style={{ color: C.textPrimary }}>Попробовать снова</Typography>
              </TouchableOpacity>
            </View>
          ) : items.length === 0 ? (
            <View
              testID="notificationsEmpty"
              style={[
                // CORE-1733 (возврат QA) — пустое состояние — поверхность v4, не полупрозрачное стекло .52.
                s.stateBox, s.emptyCard, surfaceFill(isDark ? 'dark' : 'light'),
                WEB && ({ boxShadow: cardShadow } as any),
              ]}
            >
              <View style={[s.emptyIcon, { backgroundColor: isDark ? 'rgba(233,130,69,.10)' : 'rgba(244,174,125,.16)' }]}>
                <Ionicons name={filter === 'unread' ? 'checkmark-done-outline' : 'notifications-outline'} size={28} color={ACCENT_TEXT} />
              </View>
              <Typography variant="h5" align="center" style={{ color: C.textPrimary }}>{filter === 'unread' ? 'Всё прочитано' : 'Пока тихо'}</Typography>
              <Typography variant="body" align="center" style={{ color: C.textMuted }}>
                {filter === 'unread' ? 'Новых уведомлений сейчас нет.' : 'Здесь появятся новости о ваших программах, событиях и Live-сессиях.'}
              </Typography>
            </View>
          ) : (
            <View style={s.list} testID="notificationsList">
              {groups.map((group) => (
                <View key={group.key} style={s.group} testID={`notificationsGroup-${group.key}`}>
                  <Typography variant="body" style={{ color: C.textMuted }}>{group.label}</Typography>
                  <View style={s.groupRows}>
                    {group.items.map((n) => {
                      const route = resolveNotificationRoute(n, ROUTE_CTX);
                      const canOpen = isActionable(n, ROUTE_CTX);
                      const pending = pendingId === n.id;
                      const menuOpen = menuId === n.id;
                      return (
                        <View key={n.id} style={[s.rowWrap, menuOpen && s.rowWrapRaised]}>
                          <TouchableOpacity
                            onPress={() => void open(n)}
                            disabled={pending || (!canOpen && n.isRead)}
                            activeOpacity={canOpen ? 0.76 : 1}
                            accessibilityRole={canOpen ? 'button' : undefined}
                            accessibilityLabel={`${n.title}. ${n.body}`}
                            {...(Platform.OS === 'web' && pending ? ({ 'aria-busy': true } as any) : {})}
                            testID={`notification-${n.id}`}
                            style={[
                              s.row, compact && s.rowCompact,
                              {
                                backgroundColor: n.isRead ? readSurface : cardSurface,
                                borderColor: n.isRead ? cardBorder : C.solar,
                                borderLeftWidth: n.isRead ? 1 : 3,
                                opacity: pending ? 0.62 : 1,
                              },
                              WEB && ({
                                backdropFilter: 'blur(18px) saturate(140%)',
                                WebkitBackdropFilter: 'blur(18px) saturate(140%)',
                                boxShadow: cardShadow,
                                transition: 'background-color 180ms ease, border-color 180ms ease, transform 180ms ease',
                                ...(canOpen && !pending ? { cursor: 'pointer' } : {}),
                              } as any),
                            ]}
                          >
                            <View
                              style={[
                                s.icon, compact && s.iconCompact,
                                {
                                  backgroundColor: isDark ? 'rgba(120, 84, 58, 0.22)' : 'rgba(249, 212, 181, 0.48)',
                                  borderColor: isDark ? 'rgba(241,181,142,.16)' : 'rgba(214,153,113,.12)',
                                },
                                WEB && ({
                                  backgroundImage: isDark
                                    ? 'radial-gradient(circle at 35% 25%, rgba(244,190,151,.17), rgba(76,58,45,.12) 70%)'
                                    : 'radial-gradient(circle at 35% 25%, rgba(255,255,255,.72), rgba(247,202,166,.32) 72%)',
                                } as any),
                              ]}
                            >
                              <Ionicons name={notificationIcon(n.type) as any} size={compact ? 20 : 23} color={toneColor(notificationTone(n.type))} />
                            </View>

                            <View style={s.body}>
                              <Typography variant="bodyLarge" style={{ color: C.textPrimary }} numberOfLines={2}>{n.title}</Typography>
                              {!!n.body && <Typography variant="body" style={{ color: C.textSecondary }} numberOfLines={compact ? 3 : 2}>{n.body}</Typography>}
                              <Typography variant="caption" style={{ color: C.textMuted }}>
                                {notificationTime(n.createdAtUtc, now)}{route.kind === 'unavailable' ? ` · ${route.reason}` : ''}
                              </Typography>
                            </View>

                            {pending ? (
                              <ActivityIndicator
                                size="small"
                                color={C.textMuted}
                                style={s.menuButton}
                                testID={`notification-${n.id}-pending`}
                              />
                            ) : (
                              <TouchableOpacity
                                accessibilityRole="button" accessibilityLabel={`Действия: ${n.title}`}
                                testID={`notification-${n.id}-menu`} activeOpacity={0.65}
                                onPress={(event) => { stopEvent(event); setMenuId((current) => (current === n.id ? null : n.id)); }}
                                style={[
                                  s.menuButton,
                                  {
                                    borderColor: isDark ? 'rgba(241,181,142,.25)' : 'rgba(196,145,111,.28)',
                                    backgroundColor: isDark ? 'rgba(24,23,22,.18)' : 'rgba(255,255,255,.38)',
                                  },
                                ]}
                              >
                                <Ionicons name="ellipsis-horizontal" size={20} color={C.textPrimary} />
                              </TouchableOpacity>
                            )}
                          </TouchableOpacity>

                          {menuOpen && (
                            <View
                              testID={`notification-${n.id}-actions`}
                              style={[
                                s.actionsMenu, compact && s.actionsMenuCompact,
                                { backgroundColor: C.surfaceElevated, borderColor: C.border },
                                WEB && ({ backdropFilter: 'blur(22px)', boxShadow: C.shadowSurface } as any),
                              ]}
                            >
                              {canOpen && (
                                <TouchableOpacity accessibilityRole="menuitem" style={s.actionItem} onPress={(event) => { stopEvent(event); void open(n); }}>
                                  <Ionicons name="open-outline" size={18} color={C.textPrimary} />
                                  <Typography variant="body" style={{ color: C.textPrimary }}>Открыть</Typography>
                                </TouchableOpacity>
                              )}
                              {!n.isRead && (
                                <TouchableOpacity
                                  accessibilityRole="menuitem" testID={`notification-${n.id}-mark-read`}
                                  style={s.actionItem} onPress={(event) => { stopEvent(event); void markOneRead(n); }}
                                >
                                  <Ionicons name="checkmark-done-outline" size={18} color={ACCENT_TEXT} />
                                  <Typography variant="body" style={{ color: C.textPrimary }}>Прочитать</Typography>
                                </TouchableOpacity>
                              )}
                              {!canOpen && n.isRead && <Typography variant="caption" style={[s.noActions, { color: C.textMuted }]}>Нет доступных действий</Typography>}
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                </View>
              ))}
            </View>
          )}

          {list.hasMore && !loading && !failed && (
            <TouchableOpacity
              onPress={() => void loadMore()} disabled={loadingMore} accessibilityRole="button"
              style={[s.loadMore, { borderColor: C.border, backgroundColor: readSurface }]}
            >
              {loadingMore && <ActivityIndicator size="small" color={ACCENT_TEXT} />}
              <Typography variant="body" style={{ color: C.textPrimary }}>{loadingMore ? 'Загружаем…' : 'Показать ещё'}</Typography>
            </TouchableOpacity>
          )}
        </View>
        <Footer />
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, ...(WEB ? { minHeight: '100vh' as any } : {}) },
  scroll: { flex: 1 },
  pageFlow: { flexGrow: 1 },
  content: {
    width: '100%', maxWidth: 900, alignSelf: 'center', flexGrow: 1,
    paddingTop: 120, paddingBottom: 64, paddingHorizontal: 20,
    ...(WEB ? { minHeight: 'calc(100vh - 72px)' as any } : {}),
  },
  contentCompact: {
    paddingTop: WEB ? 92 : 20, paddingBottom: 44, paddingHorizontal: 16,
    ...(WEB ? { minHeight: 'calc(100vh - 48px)' as any } : {}),
  },
  nativeBar: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingBottom: 8 },
  nativeBack: { padding: 6 },
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 20, marginBottom: 24 },
  headCompact: { alignItems: 'flex-start', flexDirection: 'column', gap: 12, marginBottom: 18 },
  titleLine: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 14 },
  unreadBadge: { minHeight: 34, paddingHorizontal: 16, justifyContent: 'center', borderRadius: 100, borderWidth: 1 },
  readAll: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, flexShrink: 0 },
  readAllDisabled: { opacity: 0.48 },
  filters: { width: 424, height: 46, flexDirection: 'row', padding: 2, borderRadius: 24, borderWidth: 1, marginBottom: 20 },
  filtersCompact: { width: '100%', marginBottom: 18 },
  filter: { flex: 1, borderRadius: 22, borderWidth: 1, borderColor: 'transparent', alignItems: 'center', justifyContent: 'center' },
  list: { gap: 18 },
  group: { gap: 8 },
  groupRows: { gap: 10 },
  rowWrap: { position: 'relative', zIndex: 1 },
  rowWrapRaised: { zIndex: 20 },
  row: { minHeight: 94, flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 18, paddingVertical: 14, borderRadius: 17, borderWidth: 1 },
  rowCompact: { minHeight: 84, gap: 12, paddingHorizontal: 12, paddingVertical: 12 },
  icon: { width: 58, height: 58, borderRadius: 29, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  iconCompact: { width: 46, height: 46, borderRadius: 23 },
  body: { flex: 1, gap: 1, minWidth: 0 },
  menuButton: { width: 42, height: 42, borderRadius: 21, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  actionsMenu: { position: 'absolute', zIndex: 50, right: 12, top: 67, width: 210, padding: 7, borderRadius: 14, borderWidth: 1 },
  actionsMenuCompact: { top: 62, right: 6 },
  actionItem: { minHeight: 42, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 9 },
  noActions: { paddingHorizontal: 10, paddingVertical: 9 },
  stateBox: { minHeight: 190, alignItems: 'center', justifyContent: 'center', gap: 12, paddingHorizontal: 24, paddingVertical: 34 },
  emptyCard: { borderWidth: 1, borderRadius: 20, marginTop: 8 },
  emptyIcon: { width: 58, height: 58, borderRadius: 29, alignItems: 'center', justifyContent: 'center' },
  retry: { paddingHorizontal: 18, paddingVertical: 9, borderRadius: 100, borderWidth: 1, marginTop: 2 },
  loadMore: { minHeight: 44, paddingHorizontal: 18, borderRadius: 100, borderWidth: 1, alignSelf: 'center', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 20 },
});
